import numberFormat from 'number-format.js'

// sentinel (not '') so callers can strip surrounding decoration and drop the
// whole token; consume only via stripHiddenPrice / hasHiddenPrice
const HIDDEN_PRICE_MARK = '__HIDDEN_PRICE__'

export const stripHiddenPrice = (value: string): string =>
  value === HIDDEN_PRICE_MARK ? '' : value

export const hasHiddenPrice = (value: string): boolean =>
  value.includes(HIDDEN_PRICE_MARK)

interface FormatPriceProps {
  currency?: string
  price: number
  format?: string
  prefix?: string
  suffix?: string
  after?: boolean
  display?: string
  code?: string
  hidePrices?: boolean
}

const replaceHandlebars = (content = '', symbol = '', code = ''): string => {
  if (content && content.length < 1) {
    return ''
  }
  let newContent = content
  // replaceAll, not replace: a `display` carrying the same token twice would
  // otherwise render the second occurrence raw.
  if (newContent.includes('{{symbol}}'))
    newContent = newContent.replaceAll('{{symbol}}', symbol)
  if (newContent.includes('{{code}}'))
    newContent = newContent.replaceAll('{{code}}', code)
  return newContent
}

const formatPrice = ({
  currency = '',
  price,
  format = '#,##0.00',
  prefix = '',
  suffix = '',
  after = false,
  code = '',
  display = '{{symbol}}{{price}}{{code}}',
  hidePrices = false,
}: FormatPriceProps): string => {
  if (format.length === 0) {
    return ''
  }

  if (hidePrices) {
    return HIDDEN_PRICE_MARK
  }

  let prefixContent = ''
  let suffixContent = ''
  // TODO: this code should be remove when all clients use display
  if (!!suffix || !!prefix) {
    prefixContent = `${prefix} ${!after ? currency : ''}`
    suffixContent = `${suffix} ${after ? currency : ''}`
  } else {
    const priceDisplay =
      display.length > 0
        ? replaceHandlebars(display, currency, code)
        : replaceHandlebars('{{symbol}}{{price}}{{code}}', currency, code)
    const displayWithoutPrice = priceDisplay.split('{{price}}')
    const [beforePriceContent, afterPriceContent] = displayWithoutPrice
    // A `display` with no {{price}} token makes split return one element, so
    // afterPriceContent is undefined and the mask below would interpolate the
    // literal string "undefined" into the rendered price. commerce-scs runs with
    // FORCE_VALIDATION=false in every environment, so a malformed display does
    // reach here.
    prefixContent = beforePriceContent ?? ''
    suffixContent = afterPriceContent ?? ''
  }
  const formattedPrice = numberFormat(
    `${prefixContent}${format}${suffixContent}`,
    price,
    { enforceMaskSign: true },
  )
  return formattedPrice
}

export default formatPrice
